import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService } from '../auth/authService';
import { recoverPasswordSchema, RecoverPasswordFormData } from '../validations/auth';

export default function RecoverPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [resetToken, setResetToken] = useState<string>('');

  useEffect(() => {
    // Get email and reset token from navigation state or redirect back to forgot password
    if (location.state?.email && location.state?.resetToken) {
      setEmail(location.state.email);
      setResetToken(location.state.resetToken);
    } else {
      navigate('/forgot-password', { replace: true });
    }
  }, [location.state, navigate]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useForm<RecoverPasswordFormData>({
    resolver: yupResolver(recoverPasswordSchema),
    defaultValues: {
      email: email,
      resetToken: resetToken,
      newPassword: '',
      confirmPassword: '',
    },
  });

  // Update form when email and reset token are set
  useEffect(() => {
    if (email && resetToken) {
      setValue('email', email);
      setValue('resetToken', resetToken);
    }
  }, [email, resetToken, setValue]);

  const onSubmit = async (data: RecoverPasswordFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await authService.recoverPassword({
        email: data.email,
        resetToken: data.resetToken,
        newPassword: data.newPassword,
      });

      if (response.success) {
        setSuccessMessage('Password successfully reset! Redirecting to sign in...');
        // Navigate to sign in page after a short delay
        setTimeout(() => {
          navigate('/sign-in', { replace: true });
        }, 2000);
      } else {
        setErrorMessage(response.message || 'Failed to reset password. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Recover password error:', error);
      
      // Handle specific error types
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      
      if (axiosError.response?.status === 400) {
        setError('newPassword', {
          type: 'manual',
          message: 'Invalid reset token or password is too short.',
        });
      } else if (axiosError.response?.status === 422) {
        setError('root', {
          type: 'manual',
          message: 'Please check your input and try again.',
        });
      } else if (axiosError.response?.status && axiosError.response.status >= 500) {
        setErrorMessage('Server error. Please try again later.');
      } else {
        setErrorMessage(axiosError.response?.data?.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!email || !resetToken) {
    return null; // Don't render until email and reset token are loaded
  }

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 8,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Reset Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your new password below
          </Typography>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="newPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                disabled={isLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                disabled={isLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {errors.root && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.root.message}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: '#b8957b',
              color: 'white',
              fontWeight: 700,
              '&:hover': { bgcolor: '#a98c74' },
              '&:disabled': { bgcolor: '#ccc' },
              mt: 3,
              mb: 2
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>

        <Box textAlign="center" mt={2}>
          <Link
            href="/forgot-password"
            variant="body2"
            sx={{
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Back to Forgot Password
          </Link>
        </Box>
      </Box>
    </Container>
  );
}
